import { Component, computed, inject, signal } from '@angular/core';
import { DEFAULT_SHOPIFY_MCP_GOAL_ID } from '../../../../../core/ai-assistant.constants';

import { firstValueFrom, map, shareReplay, switchMap } from 'rxjs';
import {
  ConnectionResponse,
  ConnectionsApiService,
  GetIntegrationMarketingRequest,
  IntegrationMarketingApiService,
  IntegrationMarketingResponse,
  ListConnectionRequest,
  ListConnectionRequestFilters,
  ListConnectionResponse,
  SupportedContexts,
} from '@vendasta/platform-integrations';
import { CustomCapabilityConfigurationForm } from '../../../../../core/forms';
import { KeyValuePair } from '@vendasta/ai-assistants';
import { NAMESPACE_CONFIG_TOKEN } from '../../../../../core/tokens';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';

export const KNOWN_MCP_GOAL_INTEGRATION_TYPES = [
  {
    goalId: DEFAULT_SHOPIFY_MCP_GOAL_ID,
    integrationType: 'Shopify',
    integrationIdDemo: 'd226e913-46ed-4f10-b9b0-e711fd46251d',
    integrationIdProd: '4eacfb1f-4665-41e3-85ff-5bdcfa72654e',
  },
];

@Component({
  selector: 'ai-mcp-integration-capability-form',
  imports: [GalaxyFormFieldModule, MatSelectModule, TranslateModule],
  templateUrl: './mcp-integration-capability-form.component.html',
  styleUrl: './mcp-integration-capability-form.component.scss',
})
export class McpIntegrationCapabilityFormComponent extends CustomCapabilityConfigurationForm {
  private readonly namespaceConfig$ = inject(NAMESPACE_CONFIG_TOKEN);
  private connectionsService = inject(ConnectionsApiService);
  private integrationService = inject(IntegrationMarketingApiService);
  protected integration = signal<IntegrationMarketingResponse | null>(null);
  protected connections = signal<ConnectionResponse[]>([]);
  private env = inject(EnvironmentService);

  ngOnInit() {
    this.loadIntegration();
  }

  async loadIntegration() {
    const integrationInfo = KNOWN_MCP_GOAL_INTEGRATION_TYPES.find((item) => item.goalId === this.goalId());
    if (!integrationInfo) {
      return;
    }

    let integrationId = integrationInfo.integrationIdDemo;
    if (this.env.getEnvironment() == Environment.PROD) {
      integrationId = integrationInfo.integrationIdProd;
    }
    const integrationMarketing = await firstValueFrom(
      this.integrationService.get(
        new GetIntegrationMarketingRequest({
          integrationId: integrationId,
        }),
      ),
    );
    this.integration.set(integrationMarketing);

    const connections = await firstValueFrom(
      this.namespaceConfig$.pipe(
        switchMap((namespaceConfig) => {
          const context = namespaceConfig.namespace?.partnerNamespace
            ? SupportedContexts.PI_CONTEXT_PARTNER
            : SupportedContexts.PI_CONTEXT_SMB;
          const namespaceID = namespaceConfig.namespace?.partnerNamespace
            ? namespaceConfig.namespace.partnerNamespace.partnerId
            : namespaceConfig.namespace?.accountGroupNamespace?.accountGroupId;

          return this.connectionsService.list(
            new ListConnectionRequest({
              namespace: namespaceID,
              filters: new ListConnectionRequestFilters({
                context: context,
                integrationType: integrationInfo.integrationType,
              }),
            }),
          );
        }),
        map((response: ListConnectionResponse) => {
          return response.connections || [];
        }),
        shareReplay(1),
      ),
    );
    this.connections.set(connections);
  }

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly selectedConnection = computed(() => {
    const configuration = this.configurationForm().value;

    if (!configuration || !Array.isArray(configuration)) {
      return null;
    }

    const connectionId = configuration.find((pair) => pair.key === 'connection_id');
    if (connectionId) {
      return this.connections().find((connection) => connection.connectionId === connectionId.value);
    }

    return null;
  });

  protected readonly compareConnections = (
    option1: ConnectionResponse | null,
    option2: ConnectionResponse | null,
  ): boolean => {
    if (!option1 || !option2) return false;

    return option1.connectionId === option2.connectionId;
  };

  protected onSelection(event: { value: ConnectionResponse | null }) {
    if (event.value) {
      this.configurationForm().setValue(this.getFormMutationsFromSelection(event.value));
    } else {
      this.configurationForm().setValue(null);
    }
  }

  protected getFormMutationsFromSelection(selection: ConnectionResponse): KeyValuePair[] {
    return [new KeyValuePair({ key: 'connection_id', value: selection.connectionId })];
  }

  protected goToIntegration(integrationId: string) {
    this.namespaceConfig$.subscribe((namespaceConfig) => {
      // Business App Redirect
      let integrationPage = `../../../../administration/integrations/${integrationId}`;
      if (namespaceConfig?.namespace?.partnerNamespace) {
        // Partner Center Redirect
        integrationPage = `../../../../integrations/${integrationId}`;
      }
      this.router.navigate([integrationPage], {
        relativeTo: this.route,
      });
    });
  }
}
